/**
 * Interface for the main page. Displays the set of map services to which the user has access, delineated by group, as well as the set of layer configurations to which they have access and, if they are in the HBK organization, the employee directory.
 * ## Fields
 * Name | Description
 * --- | ---
 * layouts | The `LayoutCollection` containing layouts to display.
 * services | The `MapServiceCollection` containing available map services.
 * @module app/views/Main/Index
 *
 */
import Backbone from "backbone";
import _ from "underscore";
import Field from "core/models/Field";
import CollectionTableView from "core/views/CollectionTableView";
import $ from "jquery";
import MapService from "app/models/MapService";
import GroupCollection from "app/models/GroupCollection";
import Permission from "app/models/Permission";
import PermissionUtil from "@HBKEngineering/permission-util";
import Organization from "app/models/Organization";
import TabView from "core/views/TabView";
import viewTemplate from "views/main/Index";
import ErrorUtils from "app/utils/ErrorUtils";
import session from "app/Session";
import urlService from "app/UrlService";
import messenger from "app/Messenger";
import s from "underscore.string";
export default Backbone.View.extend({
  template: viewTemplate,
  className: "main-index",

  /**
   * Initializes the view.
   * @param  {object} options The options hash.
   * @param {LayoutCollection} options.layouts The value for the `layouts` property.
   * @param {MapServiceCollection} options.services The value for the `services` property.
   */
  initialize: function (options) {
    this.options = options;

    this.layouts = this.options.layouts;
    this.services = this.options.services;

    if (this.options.el) {
      this.tabView = new TabView({
        el: this.$(".tab-view"),
        prefix: "service-tab",
      });
    }
  },

  _renderTabs: function (tabs) {
    var tabView = new TabView({
      tabs: tabs,
      prefix: "service-tab",
    });

    tabView.render();

    return tabView;
  },

  _renderTab: function (title, services) {
    var fields = [
      new Field(MapService.fields["name"]).decorate({
        viewOptions: {
          type: "link",
          linkField: "name",
          linkUrl: "map/service",
        },
        filterable: "name",
        id: "id",
        name: "id",
      }),
    ];
    var view = new CollectionTableView({
      fields: fields,
      collection: services,
      allowSave: false,
      allowDelete: false,
      mode: "view",
    });

    view.render();

    return {
      view: view,
      title: title,
      start: function () {},
    };
  },

  /**
   * Creates the mapping of services to render into the tab control.
   * @private
   * @return Promise object which resolves with the tabs when created, or an error upon failure.
   */
  _createServices: function () {
    var groups = new GroupCollection([]);
    var self = this;
    var deferred = new $.Deferred();
    //we want to construct a set of tabs such that each displays the services available to
    //the tab's namesake. The "Public" tab should show all services that are available to everyone.
    //following this, each tab for a group the user is in should show any services that are available
    //for members of that group. lastly, the tab for user services should display any services that
    //have been shared specifically to a user (not one of their groups)
    groups.fetch({
      success: function () {
        var userRoles = session.getRoles();
        var searchRoles = _.filter(groups.models, function (group) {
          return _.contains(userRoles, group.get("name"));
        });
        var searchModels = searchRoles.slice();
        var services = self.services.models.slice();
        var filteredServices;

        searchModels.push(session.user);
        searchModels.unshift(Permission);
        Permission.find(searchModels, false, function (err, results) {
          if (err) {
            return messenger.error(err);
          }

          var tabs = [];
          var filterFunction = function (permSet, action, service) {
            return PermissionUtil.hasAction(
              permSet,
              {
                MapService: service.id,
              },
              action
            );
          };

          filteredServices = _.filter(
            services,
            _.partial(filterFunction, results[Permission.id], "read")
          );

          if (filteredServices.length) {
            tabs.push(self._renderTab("Public", filteredServices));
          }

          _.each(searchRoles, function (group) {
            var permSet = results[group.id];

            if (permSet) {
              filteredServices = _.filter(
                services,
                _.partial(filterFunction, permSet, "read")
              );

              if (filteredServices.length) {
                tabs.push(self._renderTab(group.get("name"), filteredServices));
              }
            }
          });

          filteredServices = _.filter(services, function (service) {
            var permSet = results[session.user.id];

            return (
              filterFunction(permSet, "read", service) &&
              !filterFunction(permSet, "own", service)
            );
          });

          if (filteredServices.length) {
            tabs.push(self._renderTab("Shared to Me", filteredServices));
          }

          filteredServices = _.filter(
            services,
            _.partial(filterFunction, results[session.user.id], "own")
          );

          if (filteredServices.length) {
            tabs.push(self._renderTab("My Services", filteredServices));
          }

          deferred.resolve(tabs);
        });
      },
      error: function (collection, response, options) {
        deferred.reject(
          ErrorUtils.handleApiError(
            {
              activity: "loading groups",
            },
            collection,
            response,
            options
          )
        );
      },
    });

    return deferred.promise();
  },

  render: function () {
    var isHBK =
      session.user.attributes.Organization &&
      session.user.attributes.Organization.id == Organization.HBK;
    var self = this;

    this.$el.html(
      this.template(
        _.defaults(
          {
            layouts: this.layouts.models,
            services: [],
            isHBK: isHBK,
            urlBuilder: urlService.rootBuilder,
            s: s,
          },
          this.options
        )
      )
    );

    this._createServices().done(function (tabs) {
      var container = self.$(".service-container");

      self.tabView = self._renderTabs(tabs);

      container.children().remove();
      container.append(self.tabView.$el);
    });
  },

  /**
   * Select the desired by tab, passing either a numeric index or the title of the tab.
   * @param {Number|String} indexOrTitle The index or title of the service tab to select.
   */
  selectServiceTab: function (indexOrTitle) {
    if (this.tabView) {
      if (indexOrTitle * 1 != indexOrTitle) {
        indexOrTitle = _.findIndex(this.tabView.options.tabs, function (
          tabInfo
        ) {
          return tabInfo.title == indexOrTitle;
        });
      } else {
        indexOrTitle *= 1;
      }

      this.tabView.currentTab = indexOrTitle;
    }
  },

  /**
   * The tab view containing services in this view.
   * @type {app/views/TabView}
   */
  tabView: null,
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/main/Index.js
// module id = 939
// module chunks = 1